import { useEffect, useState, type ReactNode } from "react"
import { AlertCircle, Phone01, RefreshCcw01 } from "@untitledui/icons"
import { publicConsumerApi, type PublicShopProfile } from "@/lib/api/public-consumer"

// Public shop loader + the gate screens that sit in front of the flow:
// loading spinner, dead link (404 / inactive), shop disabled ("not taking
// online requests — call us"), and the expired-continuation screen. The flow
// itself only ever renders once the gate lets a live shop through.

export type PublicShopStatus = "loading" | "ready" | "dead"

export function usePublicShop(shopId: string | undefined) {
  const [shop, setShop] = useState<PublicShopProfile | null>(null)
  const [status, setStatus] = useState<PublicShopStatus>("loading")

  useEffect(() => {
    if (!shopId) {
      setStatus("dead")
      return
    }
    let alive = true
    setStatus("loading")
    publicConsumerApi
      .getShop(shopId)
      .then((s) => {
        if (!alive) return
        setShop(s)
        setStatus("ready")
      })
      .catch(() => {
        // 404 / inactive shop → friendly dead-link screen, never a /login bounce.
        if (!alive) return
        setStatus("dead")
      })
    return () => {
      alive = false
    }
  }, [shopId])

  return { shop, status }
}

/* ── Gate screens ─────────────────────────────────────────────────────── */

function GateShell({ children }: { children: ReactNode }) {
  return (
    <div className="grid min-h-[100dvh] place-items-center bg-gradient-to-b from-zinc-50 to-white px-6 pb-[env(safe-area-inset-bottom)]">
      <div className="w-full max-w-sm text-center">{children}</div>
    </div>
  )
}

export function ShopGateLoading() {
  return (
    <div className="grid min-h-[100dvh] place-items-center bg-gradient-to-b from-zinc-50 to-white">
      <span
        className="size-6 animate-spin rounded-full border-2 border-zinc-200 border-t-zinc-700"
        role="status"
        aria-label="Loading"
      />
    </div>
  )
}

export function ShopGateDead() {
  return (
    <GateShell>
      <div className="mx-auto grid size-12 place-items-center rounded-full bg-amber-50">
        <AlertCircle className="size-6 text-amber-600" aria-hidden />
      </div>
      <h1 className="mt-4 text-[20px] font-bold tracking-tight text-zinc-900">
        This link isn't active
      </h1>
      <p className="mt-2 text-[14px] leading-relaxed text-zinc-500">
        The estimate link you opened isn't available right now. Double-check the
        link from your shop, or reach out to them directly.
      </p>
    </GateShell>
  )
}

export function ShopGateDisabled({ shop }: { shop: PublicShopProfile }) {
  return (
    <GateShell>
      <div className="mx-auto grid size-12 place-items-center rounded-full bg-zinc-100">
        <Phone01 className="size-6 text-zinc-500" aria-hidden />
      </div>
      <h1 className="mt-4 text-[20px] font-bold tracking-tight text-zinc-900">
        {shop.name} isn't taking online requests right now
      </h1>
      <p className="mt-2 text-[14px] leading-relaxed text-zinc-500">
        {shop.phoneNumber
          ? `Give them a call at ${shop.phoneNumber} and they'll get you sorted.`
          : "Reach out to the shop directly and they'll get you sorted."}
      </p>
      {shop.phoneNumber && (
        <a
          href={`tel:${shop.phoneNumber.replace(/[^\d+]/g, "")}`}
          className="mt-6 inline-flex h-14 w-full items-center justify-center gap-2 rounded-2xl bg-zinc-900 text-[15px] font-semibold text-white shadow-lg shadow-zinc-900/20 transition-colors hover:bg-black"
        >
          <Phone01 className="size-4" aria-hidden />
          Call {shop.phoneNumber}
        </a>
      )}
    </GateShell>
  )
}

export function ShopGateExpired({
  onStartNew,
  onTrack,
}: {
  onStartNew: () => void
  onTrack: (() => void) | null
}) {
  return (
    <GateShell>
      <div className="mx-auto grid size-12 place-items-center rounded-full bg-amber-50">
        <RefreshCcw01 className="size-6 text-amber-600" aria-hidden />
      </div>
      <h1 className="mt-4 text-[20px] font-bold tracking-tight text-zinc-900">
        This link has expired
      </h1>
      <p className="mt-2 text-[14px] leading-relaxed text-zinc-500">
        Continuation links only last a few days. You can start a new request in
        under a minute, or check on one you already sent.
      </p>
      <div className="mt-6 space-y-3">
        <button
          type="button"
          onClick={onStartNew}
          className="flex h-14 w-full items-center justify-center rounded-2xl bg-zinc-900 text-[15px] font-semibold text-white shadow-lg shadow-zinc-900/20 transition-colors hover:bg-black"
        >
          Start a new request
        </button>
        {onTrack && (
          <button
            type="button"
            onClick={onTrack}
            className="h-12 w-full rounded-xl text-[14px] font-semibold text-zinc-500 transition-colors hover:text-zinc-900"
          >
            Track your request
          </button>
        )}
      </div>
    </GateShell>
  )
}

// Couldn't load a continuation (signal, server hiccup): the link may well be
// fine, so retry rather than suggesting a fresh start.
export function ShopGateRetry({ onRetry }: { onRetry: () => void }) {
  return (
    <GateShell>
      <div className="mx-auto grid size-12 place-items-center rounded-full bg-amber-50">
        <RefreshCcw01 className="size-6 text-amber-600" aria-hidden />
      </div>
      <h1 className="mt-4 text-[20px] font-bold tracking-tight text-zinc-900">
        Couldn't load your request
      </h1>
      <p className="mt-2 text-[14px] leading-relaxed text-zinc-500">
        This is usually the signal. Check your connection and try again.
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-6 flex h-14 w-full items-center justify-center rounded-2xl bg-zinc-900 text-[15px] font-semibold text-white shadow-lg shadow-zinc-900/20 transition-colors hover:bg-black"
      >
        Try again
      </button>
    </GateShell>
  )
}
